import { apiClient } from '@shared/utils/api/client';

import type { IAuthInit, IAuthLoginRequest, IAuthSessionResponse } from './types';

import { AUTH_API_PATHS } from '../config';

/**
 * AuthService — all panel auth HTTP calls.
 *
 * All users (AstroPay employees and partner agents) use the same flow:
 *   1. `authInit(email)` → POST /panel/auth-init → IAuthInit with PKCE params
 *   2. Frontend redirects to authorization_endpoint
 *   3. AS redirects back with ?code=...&state=...
 *   4. `login(code, state)` → POST /panel/login → SESSION_TOKEN + SESSION_REFRESH_TOKEN cookies
 *   5. On 401: `refresh()` → POST /panel/refresh → fresh cookies
 */
export class AuthService {
  /**
   * Initiates the OAuth 2.0 Authorization Code + PKCE flow.
   * The backend resolves the correct IDP from the email domain.
   */
  static async authInit(email: string): Promise<IAuthInit> {
    const response = await apiClient.post<IAuthInit>(AUTH_API_PATHS.authInit, { email });
    return response.data;
  }

  /**
   * Completes the Authorization Code + PKCE flow.
   * The backend retrieves the code_verifier server-side by state.
   */
  static async login(code: string, state: string): Promise<IAuthSessionResponse> {
    const requestData: IAuthLoginRequest = { code, state };
    const response = await apiClient.post<IAuthSessionResponse>(AUTH_API_PATHS.login, requestData);
    return response.data;
  }

  /** Returns the current session state. */
  static async getCurrentSession(): Promise<IAuthSessionResponse> {
    const response = await apiClient.get<IAuthSessionResponse>(AUTH_API_PATHS.me);
    return response.data;
  }

  /**
   * Refreshes the current session.
   * On success the backend sets fresh SESSION_TOKEN + SESSION_REFRESH_TOKEN cookies.
   */
  static async refresh(): Promise<boolean> {
    try {
      await apiClient.post(AUTH_API_PATHS.refresh, undefined, { suppressUnauthorized: true });
      return true;
    } catch {
      return false;
    }
  }

  /** Logs out and invalidates the session. */
  static async logout(): Promise<void> {
    await apiClient.delete(AUTH_API_PATHS.logout);
  }
}

export default AuthService;
